import React, { useRef } from "react";
import { motion, useInView } from "framer-motion";
import quoteIcon from "../../assets/quote-icon.svg";

const TestimonialCard = ({ item, index }) => {
  const ref = useRef(null);

  const isInView = useInView(ref, {
    once: true,
    margin: "-80px",
  });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 40 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      style={{
        transform: "translate3d(0,0,0)",
        willChange: "transform, opacity",
      }}
    >
      <div
        className={`relative h-full flex flex-col p-8 md:p-10 rounded-[32px] transition-all duration-500 overflow-hidden group hover:-translate-y-3 hover:shadow-[0_40px_80px_-20px_rgba(37,99,235,0.3)]
        ${
          item.featured
            ? "bg-gradient-to-b from-[#112240] to-[#0a1628] border border-blue-500/30 md:-translate-y-6 md:hover:-translate-y-9 shadow-[0_12px_40px_rgba(37,99,235,0.1)]"
            : "bg-white/[0.02] border border-white/[0.08] hover:bg-white/[0.04] hover:border-blue-400/40"
        }`}
      >
        {/* Quote */}
        <div className="absolute top-3 left-3 opacity-10 pointer-events-none">
          <img
            className="w-[70px]"
            src={quoteIcon}
            alt="quote"
            style={{
              transform: "translate3d(0,0,0)",
              backfaceVisibility: "hidden",
            }}
          />
        </div>

        {/* Stars */}
        <div className="flex gap-[4px] mb-8 relative z-10">
          {[...Array(5)].map((_, i) => (
            <svg
              key={i}
              className="w-5 h-5 text-yellow-400"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
            </svg>
          ))}
        </div>

        {/* Text */}
        <div className="flex-grow flex flex-col justify-between relative z-10">
          <p className="text-gray-300 text-[16px] md:text-[17px] leading-[1.8] mb-10 font-light tracking-wide">
            "{item.text}"
          </p>

          {/* Author */}
          <div>
            <div className="w-12 h-[2px] bg-blue-500/40 mb-6 rounded-full group-hover:w-full transition-all duration-700"></div>

            <div className="flex items-center gap-5">
              <div
                className="w-14 h-14 shrink-0 aspect-square rounded-full 
  bg-gradient-to-br from-blue-600 to-[#1e3a8a] 
  flex items-center justify-center 
  text-white font-bold text-lg leading-none"
              >
                {item.avatar}
              </div>

              <div className="flex flex-col">
                <h4 className="text-white font-black text-[17px] tracking-wide mb-1 group-hover:text-blue-300 transition-colors duration-300">
                  {item.name}
                </h4>

                <p className="text-white/40 text-xs font-bold uppercase tracking-widest">
                  {item.role}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const Testimonials = () => {
  const testimonials = [
    {
      text: "Sahajink has been our label partner for over 3 years. Their quality is consistently excellent and they never miss a deadline.",
      name: "Rajesh Kumar",
      role: "Procurement Head, PharmaCure Ltd.",
      avatar: "RK",
      featured: false,
    },
    {
      text: "We switched to Sahajink for our FMCG labels and the difference was immediately visible on shelves.",
      name: "Priya Mehta",
      role: "Brand Manager, NatureFresh Foods",
      avatar: "PM",
      featured: true,
    },
    {
      text: "Outstanding service! We needed 50,000 cosmetic labels in 3 days and Sahajink delivered.",
      name: "Sunita Kapoor",
      role: "Operations Director, GlowUp Cosmetics",
      avatar: "SK",
      featured: false,
    },
  ];

  return (
    <section id="testimonials" className="py-20 sm:py-24 bg-[#0a1628]">
      <div className="max-w-[1200px] mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16 sm:mb-20">
          <h2 className="text-4xl md:text-5xl font-black text-white mb-6">
            Trusted by Industry Leaders
          </h2>

          <p className="text-white/40 max-w-[650px] mx-auto text-[16px] sm:text-lg">
            See what our partners say about our printing quality and
            reliability.
          </p>
        </div>

        {/* Grid */}
        <div className="grid md:grid-cols-3 gap-8">
          {testimonials.map((item, i) => (
            <TestimonialCard key={i} item={item} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
